import { useLingui } from "@lingui/react";
import { Trans } from "@lingui/react/macro";
import { Box, Divider, lighten, Stack, Typography, useTheme } from "@mui/material";
import { useBookingContext } from "@/contexts/BookingContext";
import { useFormatPrice } from "@/hooks";
import { formatPaymentInfo } from "@/utils/pricing";
import { CancellationTermsContent } from "./CancellationTerms";
import { PaymentInfoContent } from "./PaymentInfo";
import PriceBreakdown from "./PriceBreakdown";

interface BookingPriceDetailsContentProps {
  showTitle?: boolean;
}

export default function BookingPriceDetailsContent({
  showTitle = true,
}: BookingPriceDetailsContentProps) {
  const { state } = useBookingContext();
  const { i18n } = useLingui();
  const price = state.price;
  const { formatPrice, formatPriceAlt } = useFormatPrice();
  const theme = useTheme();

  const hasCancellationTerms = (price?.cancellation_terms || []).length > 0;
  const hasPaymentInfo = Boolean(formatPaymentInfo(price?.payment_system || "", i18n));

  return (
    <Stack spacing={2}>
      <div>
        {showTitle && (
          <Typography variant="h3" gutterBottom>
            <Trans>Price Details</Trans>
          </Typography>
        )}
        {price && <PriceBreakdown price={price} />}
        <Box
          sx={{ bgcolor: lighten(theme.palette.primary.main, 0.8), mx: -3, px: 3, py: 1.5, mt: 2 }}
        >
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
            <Typography variant="body1" fontWeight={600}>
              <Trans>Total price</Trans>
            </Typography>
            <Stack alignItems="flex-end">
              <Typography variant="body1" fontWeight={600}>
                {formatPrice(price?.total || 0)}
              </Typography>
              {price?.currency_alt && (
                <Typography variant="body2" color="text.secondary">
                  ({formatPriceAlt(price.currency_alt.total)})
                </Typography>
              )}
            </Stack>
          </Stack>
        </Box>
      </div>

      <Stack divider={<Divider />} spacing={2}>
        {hasCancellationTerms && (
          <Box>
            <Typography variant="h4" gutterBottom>
              <Trans>Cancellation Policy</Trans>
            </Typography>
            <CancellationTermsContent />
          </Box>
        )}

        {hasPaymentInfo && (
          <Box>
            <Typography variant="h4" gutterBottom>
              <Trans>Payment Information</Trans>
            </Typography>
            <PaymentInfoContent />
          </Box>
        )}
      </Stack>
    </Stack>
  );
}
